import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const InstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      
      // Show prompt after 3 seconds if not dismissed before
      const dismissed = localStorage.getItem('skybot_install_dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS prompt after 5 seconds
    if (isIOSDevice) {
      const dismissed = localStorage.getItem('skybot_install_dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowPrompt(false);
        setIsInstalled(true);
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('skybot_install_dismissed', 'true');
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm"
      >
        <div 
          className="rounded-2xl p-4 shadow-2xl"
          style={{
            background: theme.colors.surface,
            border: `1px solid ${theme.colors.borderActive}`,
            boxShadow: `0 10px 40px rgba(0,0,0,0.5), 0 0 30px ${theme.colors.primary}20`
          }}
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 transition-colors"
            style={{ color: theme.colors.textMuted }}
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-4">
            {/* Icon */}
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ 
                background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
                boxShadow: `0 4px 20px ${theme.colors.primary}40`
              }}
            >
              <Smartphone className="w-7 h-7" style={{ color: theme.colors.void }} />
            </div>

            {/* Content */}
            <div className="flex-1 pr-4">
              <h3 
                className="font-unbounded font-bold text-sm mb-1"
                style={{ color: theme.colors.text }}
              >
                Install Skybot
              </h3>
              
              {isIOS ? (
                <p className="text-xs mb-3" style={{ color: theme.colors.textMuted }}>
                  Tap <span className="inline-flex items-center px-1 py-0.5 rounded bg-white/10">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L8 6h3v8h2V6h3L12 2zm-7 10v10h14V12h-4v2h2v6H7v-6h2v-2H5z"/>
                    </svg>
                  </span> then "Add to Home Screen"
                </p>
              ) : (
                <p className="text-xs mb-3" style={{ color: theme.colors.textMuted }}>
                  Add Skybot to your home screen for quick access
                </p>
              )}

              {!isIOS && (
                <button
                  onClick={handleInstall}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-105"
                  style={{
                    background: theme.colors.primary,
                    color: theme.colors.void
                  }}
                >
                  <Download className="w-3 h-3" />
                  Install App
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
